import mitt from "mitt";
import type { ResponseInterface } from "~~/api/auth/interfaces";
import type { ApiResponseInterface } from "~~/interfaces/errorHandler";
import { useAuthStore } from "~~/store/auth";
import { errorHandler } from "./errorHandler";

export const fetchEvents = mitt();

type methodType = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";

interface RequestOptionsInterface {
  method: methodType;
  body?: string | object;
}

export const fetchWrapper = {
  get: request("GET"),
  post: request("POST"),
  put: request("PUT"),
  patch: request("PATCH"),
  delete: request("DELETE"),
};

interface FetchWrapperOptions {
  showToast?: boolean;
  showErrorToast?: boolean;
  toastMessage?: string;
  toastErrorMessage?: string;
  toastParams?: Record<string, unknown>;
}

type FetchErrorLike = {
  response?: {
    status?: number;
    _data?: object;
    data?: object;
    statusText?: string;
    body?: object;
  };
  status?: number;
  statusCode?: number;
  status_code?: number;
  data?: object;
  _data?: object;
  message?: string;
};

function buildQueryString(params?: Record<string, unknown>): string {
  if (!params) return "";

  const queryParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      queryParams.append(key, value.toString());
    }
  });

  const queryString = queryParams.toString();
  return queryString ? `?${queryString}` : "";
}

function request(method: methodType) {
  return async (
    endpoint: string,
    body?: object,
    queryParams?: Record<string, unknown>,
    t?: (key: string, params?: Record<string, unknown>) => string,
    options?: FetchWrapperOptions,
  ) => {
    const { public: config } = useRuntimeConfig();
    const apiUrl = config.apiUrl;
    const isMultipart = body instanceof FormData;

    const fullEndpoint = `${endpoint}${buildQueryString(queryParams)}`;

    const requestOptions: RequestOptionsInterface = {
      method,
    };

    if (body) {
      try {
        requestOptions.body = isMultipart ? body : JSON.stringify(body);
      } catch {
        console.error("Invalid body format in request");
        return;
      }
    }

    const headers: Record<string, string> = {
      Accept: "application/json",
    };

    if (!isMultipart) {
      headers["Content-Type"] = "application/json";
    }

    const { $i18n } = useNuxtApp();
    const locale = $i18n?.locale.value || "it";
    headers["Accept-Language"] = locale;

    try {
      const response: unknown = await $fetch.raw(`${apiUrl}${fullEndpoint}`, {
        ...requestOptions,
        headers,
        credentials: "include",
      });

      if (method !== "GET" && options?.showToast) {
        let defaultMessage = options.toastMessage;
        if (!defaultMessage) {
          if (method === "POST")
            defaultMessage = "generic.created_successfully";
          else if (method === "PATCH" || method === "PUT")
            defaultMessage = "generic.updated_successfully";
          else if (method === "DELETE")
            defaultMessage = "generic.deleted_successfully";
        }

        fetchEvents.emit("success", {
          endpoint,
          method,
          message: defaultMessage,
          params: options.toastParams,
        });
      }

      return response as ApiResponseInterface;
    } catch (error: unknown) {
      const translate = t || ((k: string) => k);
      const err = error as FetchErrorLike;
      const shouldShowErrorToast =
        options?.showErrorToast ?? options?.showToast !== false;

      const normalizedResponse: ResponseInterface = {
        status:
          (err?.response?.status as number) ||
          (err?.status as number) ||
          (err?.statusCode as number) ||
          (err?.status_code as number) ||
          0,
        ok: false,
        statusText: (err?.response?.statusText as string) || err?.message || "",
        _data:
          (err?.response?._data as object) ||
          (err?.data as object) ||
          (err?._data as object) ||
          (err?.response?.data as object) ||
          (err?.response?.body ? (err.response!.body as object) : undefined) ||
          undefined,
      };
      if (!normalizedResponse._data && err?.message) {
        normalizedResponse._data = { error: err.message } as object;
      }

      // If 401, try refreshing token once and retry the original request
      if (normalizedResponse.status === 401) {
        try {
          const authStore = useAuthStore();
          // Avoid attempting refresh if the request was itself the refresh endpoint
          if (!endpoint.includes("token/refresh")) {
            const refreshed = await authStore.refreshToken({
              showToast: false,
            });
            if (refreshed) {
              // Retry the original request once without showing toasts
              try {
                const retryResponse: unknown = await $fetch.raw(
                  `${apiUrl}${fullEndpoint}`,
                  {
                    ...requestOptions,
                    headers,
                    credentials: "include",
                  },
                );
                return retryResponse as ApiResponseInterface;
              } catch {
                // Fall through to error handler below
                const parsedError = await errorHandler(
                  normalizedResponse,
                  translate,
                );
                if (shouldShowErrorToast && !parsedError.skipToast) {
                  fetchEvents.emit("error", {
                    endpoint,
                    method,
                    error: parsedError.message,
                    message: options?.toastErrorMessage || parsedError.message,
                    params: options?.toastParams,
                  });
                }
                throw parsedError.message;
              }
            }
          }
        } catch (refreshErr) {
          // If refresh throws, proceed to normal error handling below
          console.warn("Token refresh attempt failed:", refreshErr);
        }
      }

      const parsedError = await errorHandler(normalizedResponse, translate);

      // Non mostrare il toast se skipToast è true (token refreshato automaticamente)
      if (shouldShowErrorToast && !parsedError.skipToast) {
        fetchEvents.emit("error", {
          endpoint,
          method,
          error: parsedError.message,
          message: options?.toastErrorMessage || parsedError.message,
          params: options?.toastParams,
        });
      }
      throw parsedError.message;
    }
  };
}
